import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Estado vazio padrão. `sm` é uma linha discreta dentro de um painel/lista
 * (substitui os `<p>Nenhum…</p>` soltos); `md` é o bloco com ícone, título,
 * descrição e ação para telas inteiras. Não decide quando está vazio — o
 * pai renderiza quando a lista chegou vazia (não durante loading).
 */
export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  /** Ícone 20px (lucide). Só em `md`. */
  icon?: ReactNode;
  /** Botão/link de próxima ação. Só em `md`. */
  action?: ReactNode;
  size?: "sm" | "md";
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  size = "md",
  className,
}: EmptyStateProps) {
  if (size === "sm") {
    return (
      <div
        role="status"
        className={cn("py-6 text-center text-sm text-muted-foreground", className)}
      >
        <p>{title}</p>
        {description && <p className="mt-1 text-xs">{description}</p>}
      </div>
    );
  }
  return (
    <div
      role="status"
      className={cn("flex flex-col items-center justify-center px-6 py-10 text-center", className)}
    >
      {icon && (
        <span
          aria-hidden="true"
          className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground"
        >
          {icon}
        </span>
      )}
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {description && (
        <p className="mt-1 max-w-sm text-[13px] text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
